import React from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ErrorCard from '../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import GenericCard from '../../../../shared/components/v3/Cards/GenericCard/GenericCard';
import ExpansiveContainer from '../../../../shared/components/v3/ExpansiveContainer/ExpansiveContainer';
import type { BenefitsState } from '../../../../shared/interfaces/collaborators';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';

import './Benefits.scss';

interface IBenefits {
  benefitsData: BenefitsState;
}

type categoryKeys = 'wellbeing' | 'education' | 'recognition' | 'finance';

const Benefits = ({ benefitsData }: IBenefits) => {
  const { t } = useTranslation();

  const TRANSLATIONS_BENEFIT_CATEGORIES: { [key in categoryKeys]: string } = {
    wellbeing: 'global.wellbeing.main',
    education: 'global.learning',
    recognition: 'global.recognition.main',
    finance: 'global.finance.main'
  };

  if (benefitsData.isLoading) {
    return (
      <div className="ben-container">
        <Skeleton className="ben-skeleton" variant="rectangular" />
      </div>
    );
  }

  if (benefitsData.error) {
    return (
      <div className="ben-error-container p-8">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
        />
      </div>
    );
  }

  return (
    <div className="ben-container flex flex-col gap-8">
      {Object.keys(benefitsData.data).map((category: string) => {
        const categoryBenefits = benefitsData.data[category];
        const numberOfBenefits = categoryBenefits?.length;

        return (
          <div key={category} className="ben-cards-container flex flex-col p-8 gap-8" data-experience="new">
            <div className="flex flex-row gap-4 items-baseline">
              <div className="ben-title">{t(TRANSLATIONS_BENEFIT_CATEGORIES[category as categoryKeys])}</div>
              <span className="availables">
                {t(TRANSLATIONS_GLOBAL.totalWellbeingBenefits, {
                  count: numberOfBenefits,
                  total_benefits: numberOfBenefits
                })}
              </span>
            </div>
            <ExpansiveContainer>
              {categoryBenefits.map((benefit) => (
                <GenericCard
                  key={benefit.benefitId}
                  title={benefit.title}
                  description={benefit.providerName}
                  icon={benefit.icon}
                  size="m"
                />
              ))}
            </ExpansiveContainer>
          </div>
        );
      })}
    </div>
  );
};

export default Benefits;
